import { useState } from 'react';
import round from '../../../helpers/round';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const HansenResultDepthFactors = ({ result }) => {
  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const {
    canUserSeeCalculationSteps,
    phips,
    isInclinedLoading,
    isPhiZero,
    k,
    kb,
    kl,
    dc,
    dcb,
    dcl,
    dq,
    dqb,
    dql,
    dGamma,
    dGammab,
  } = result.output;

  const case1 = isInclinedLoading && isPhiZero;
  const case2 = isInclinedLoading && !isPhiZero;
  const case3 = !isInclinedLoading && isPhiZero;
  const case4 = !isInclinedLoading && !isPhiZero;
  const caseLegendItems = [
    { label: 'CASE 1', description: 'Inclined loading with \u03c6 = 0', isActive: case1 },
    { label: 'CASE 2', description: 'Inclined loading with \u03c6 \u2260 0', isActive: case2 },
    { label: 'CASE 3', description: 'No inclined loading with \u03c6 = 0', isActive: case3 },
    { label: 'CASE 4', description: 'No inclined loading with \u03c6 \u2260 0', isActive: case4 },
  ];
  const [showCase13dcDetails, setShowCase13dcDetails] = useState(false);
  const [showCase2dqDetails, setShowCase2dqDetails] = useState(false);
  const [showCase2dcDetails, setShowCase2dcDetails] = useState(false);
  const [showCase2dgammaDetails, setShowCase2dgammaDetails] = useState(false);
  const [showCase4dqDetails, setShowCase4dqDetails] = useState(false);
  const [showCase4dcDetails, setShowCase4dcDetails] = useState(false);
  const [showCase4dgammaDetails, setShowCase4dgammaDetails] = useState(false);

  const renderCaseBadge = (label, sx = {}) => (
    <Box
      component="span"
      sx={{
        ml: 1,
        px: 1,
        py: 0.2,
        borderRadius: 999,
        border: '1px solid',
        borderColor: 'success.light',
        color: 'success.dark',
        backgroundColor: 'rgba(46, 125, 50, 0.12)',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: 0.3,
        lineHeight: 1.2,
        display: 'inline-flex',
        alignItems: 'center',
        ...sx,
      }}
    >
      {label}
    </Box>
  );

  const successAlertSx = {
    backgroundColor: 'rgba(46,125,50,0.04)',
    alignItems: 'flex-start',
    '& .MuiAlert-icon': {
      alignSelf: 'flex-start',
      mt: 0.25,
      minWidth: 24,
    },
    '& .MuiAlert-action': {
      alignSelf: 'flex-start',
      mt: 0.25,
      width: 32,
      justifyContent: 'center',
      p: 0,
      ml: 1,
    },
    '& .MuiAlert-message': { width: '100%' },
  };

  const renderAlertAction = (isExpanded, setExpanded) =>
    canUserSeeCalculationSteps ? (
      <IconButton size="small" color="inherit" onClick={() => setExpanded((v) => !v)} sx={{ width: 32, height: 32 }}>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}
        />
      </IconButton>
    ) : (
      <Tooltip
        title={
          <span>
            Upgrade to unlock calculation details.{' '}
            <Link href="/pricing" target="_blank" rel="noopener" style={{ color: '#90caf9', textDecoration: 'underline' }}>
              See plans
            </Link>
          </span>
        }
        leaveDelay={500}
        arrow
      >
        <LockOutlinedIcon
          sx={{
            fontSize: 20,
            color: 'text.disabled',
            cursor: 'not-allowed',
            mr: 1,
            mt: 0.5,
          }}
        />
      </Tooltip>
    );

  const Case1Case3dcDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        &phi; = 0 &rarr; Only d'<sub>c</sub> should be calculated.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d'<sub>c</sub> = 0.4 k
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d'<sub>c</sub> = 0.4 ({round(k, 2)})
      </Typography>
      <Typography variant="body2" color="text.secondary">
        s'<sub>c</sub> = {round(dc, 2)}
      </Typography>
    </>
  );

  const Case2dqDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>q, B</sub> and d<sub>q, L</sub> should be calculated.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>q, B</sub> = 1 + 2tan&phi; (1 - sin&phi;)<sup>2</sup>(k<sub>B</sub>)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>q, B</sub> = 1 + 2tan{round(phips, 2)} (1 - sin{round(phips, 2)})<sup>2</sup>({round(kb, 2)})
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>q, B</sub> = {round(dqb, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>q, L</sub> = 1 + 2tan&phi; (1 - sin&phi;)<sup>2</sup>(k<sub>L</sub>)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>q, L</sub> = 1 + 2tan{round(phips, 2)} (1 - sin{round(phips, 2)})<sup>2</sup>({round(kl, 2)})
      </Typography>
      <Typography variant="body2" color="text.secondary">
        d<sub>q, L</sub> = {round(dql, 2)}
      </Typography>
    </>
  );

  const Case2dcDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>c, B</sub> and d<sub>c, L</sub> should be calculated.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>c, B</sub> = 1 + 0.4(k<sub>B</sub>)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>c, B</sub> = 1 + 0.4({round(kb, 2)})
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>c, B</sub> = {round(dcb, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>c, L</sub> = 1 + 0.4(k<sub>L</sub>)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>c, L</sub> = 1 + 0.4({round(kl, 2)})
      </Typography>
      <Typography variant="body2" color="text.secondary">
        d<sub>c, L</sub> = {round(dcl, 2)}
      </Typography>
    </>
  );

  const Case2dgammaDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        H<sub>B</sub> &ne; 0 and/or H<sub>L</sub> &ne; 0 (Inclined loading.)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>&gamma;, B</sub> and d<sub>&gamma;, L</sub> should be calculated.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        d<sub>&gamma;, B</sub> = d<sub>&gamma;, L</sub> = {round(dGammab, 2)} for all &phi;
      </Typography>
    </>
  );

  const Case4dqDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>q</sub> = 1 + 2tan&phi; (1 - sin&phi;)<sup>2</sup>(k)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>q</sub> = 1 + 2tan{round(phips, 2)} (1 - sin{round(phips, 2)})<sup>2</sup>({round(k, 2)})
      </Typography>
      <Typography variant="body2" color="text.secondary">
        d<sub>q</sub> = {round(dq, 2)}
      </Typography>
    </>
  );

  const Case4dcDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>c</sub> = 1 + 0.4(k)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>c</sub> = 1 + 0.4({round(k, 2)})
      </Typography>
      <Typography variant="body2" color="text.secondary">
        d<sub>c</sub> = {round(dc, 2)}
      </Typography>
    </>
  );

  const Case4dgammaDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        H<sub>B</sub> = H<sub>L</sub> = 0 (No inclined loading.)
      </Typography>
      <Typography variant="body2" color="text.secondary">
        d<sub>&gamma;</sub> = {round(dGamma, 2)} for all &phi;
      </Typography>
    </>
  );

  return (
    <Box sx={{ my: 2, textAlign: 'justify' }}>
      <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
        <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
          Calculation of Hansen Depth Factors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
          This section computes Hansen depth factors (d<sub>c</sub>, d<sub>q</sub>, and d<sub>&gamma;</sub>) based on load
          inclination and friction-angle conditions. The 4 cases that can happen based on these conditions are listed below:
        </Typography>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'info.light',
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={700}>
              Case Legend
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.8 }}>
            {caseLegendItems.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '1px solid',
                  borderColor: item.isActive ? 'success.main' : 'divider',
                  borderRadius: 1.5,
                  px: 1,
                  py: 0.7,
                  backgroundColor: item.isActive ? 'rgba(46,125,50,0.08)' : 'rgba(255,255,255,0.45)',
                }}
              >
                {renderCaseBadge(item.label, {
                  ml: 0,
                  minWidth: 64,
                  justifyContent: 'center',
                  borderColor: item.isActive ? 'success.main' : 'divider',
                  color: item.isActive ? 'success.dark' : 'text.disabled',
                  backgroundColor: item.isActive ? 'rgba(46, 125, 50, 0.16)' : 'rgba(0, 0, 0, 0.03)',
                  fontWeight: item.isActive ? 700 : 600,
                })}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.82rem', fontWeight: item.isActive ? 600 : 500 }}
                >
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Success Alert: Case 1/3 d'c Factor */}
          {(case1 || case3) && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase13dcDetails, setShowCase13dcDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Depth factor (d'<sub>c</sub>) = {round(dc, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase13dcDetails} unmountOnExit>
                  <Case1Case3dcDetails />
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 2 dq Factor */}
          {case2 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase2dqDetails, setShowCase2dqDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Depth factors &rarr; d<sub>q,B</sub> = {round(dqb, 2)}, d<sub>q,L</sub> = {round(dql, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase2dqDetails} unmountOnExit>
                  <Case2dqDetails />
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 2 dc Factor */}
          {case2 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase2dcDetails, setShowCase2dcDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Depth factors &rarr; d<sub>c,B</sub> = {round(dcb, 2)}, d<sub>c,L</sub> = {round(dcl, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase2dcDetails} unmountOnExit>
                  <Case2dcDetails />
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 2 d_gamma Factor */}
          {case2 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase2dgammaDetails, setShowCase2dgammaDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Depth factors &rarr; d<sub>&gamma;,B</sub> = {round(dGammab, 2)}, d<sub>&gamma;,L</sub> = {round(dGammab, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase2dgammaDetails} unmountOnExit>
                  <Case2dgammaDetails />
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 4 dq Factor */}
          {case4 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase4dqDetails, setShowCase4dqDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Depth factor (d<sub>q</sub>) = {round(dq, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase4dqDetails} unmountOnExit>
                  <Case4dqDetails />
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 4 dc Factor */}
          {case4 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase4dcDetails, setShowCase4dcDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Depth factor (d<sub>c</sub>) = {round(dc, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase4dcDetails} unmountOnExit>
                  <Case4dcDetails />
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 4 d_gamma Factor */}
          {case4 && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase4dgammaDetails, setShowCase4dgammaDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Depth factor (d<sub>&gamma;</sub>) = {round(dGamma, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase4dgammaDetails} unmountOnExit>
                  <Case4dgammaDetails />
                </Collapse>
              )}
            </Alert>
          )}
        </Box>
      </Alert>
    </Box>
  );
};
export default HansenResultDepthFactors;
